
import React from "react";
import { Helmet } from "react-helmet";
import { articleStructuredData } from "@/data/structuredData";

interface BlogPostSEOProps {
  title: string;
  excerpt: string;
  imageUrl?: string;
  slug?: string;
  author?: string;
  datePublished?: string;
  dateModified?: string;
  articleBody?: string;
  category?: string;
  tags?: string[];
  aboutTopics?: string[];
}

const BlogPostSEO: React.FC<BlogPostSEOProps> = ({ 
  title, 
  excerpt, 
  imageUrl, 
  slug,
  author = "Jörg Hörmann",
  datePublished,
  dateModified,
  articleBody,
  category,
  tags,
  aboutTopics
}) => {
  const url = `https://joerg-hoermann.de/blog/${slug}`;
  
  // Convert German date format (DD.MM.YYYY) to ISO 8601 (YYYY-MM-DD)
  const toISODate = (dateStr?: string): string | undefined => {
    if (!dateStr) return undefined;
    const parts = dateStr.split('.');
    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dateStr;
  };

  const isoDatePublished = toISODate(datePublished) || new Date().toISOString().split('T')[0];
  const isoDateModified = toISODate(dateModified);
  
  // Create article structured data with about & speakable
  const articleSchema = articleStructuredData({
    title,
    description: excerpt,
    author,
    datePublished: isoDatePublished,
    dateModified: isoDateModified,
    url,
    image: imageUrl,
    articleBody,
    category,
    tags,
    aboutTopics
  });

  return (
    <Helmet>
      <title>{title} | Jörg Hörmann - Finanzcoach & Executive Coach</title>
      <meta name="description" content={excerpt} />
      <meta name="keywords" content="Finanzcoaching, Executive Coaching, Führungskräfte Coaching, Finanzielle Bildung, Persönlichkeitsentwicklung, Jörg Hörmann, Jörg Hormann" />
      <link rel="canonical" href={url} />
      
      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={excerpt} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Jörg Hörmann - Finanzcoach & Executive Coach" />
      <meta property="og:image" content={imageUrl || "https://joerg-hoermann.de/images/jh-logo-dark.png"} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {isoDatePublished && <meta property="article:published_time" content={isoDatePublished} />}
      {isoDateModified && <meta property="article:modified_time" content={isoDateModified} />}
      <meta property="article:author" content={author} />
      {category && <meta property="article:section" content={category} />}
      {tags && tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={excerpt} />
      <meta name="twitter:image" content={imageUrl || "https://joerg-hoermann.de/images/jh-logo-dark.png"} />
      
      {/* Article Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
    </Helmet>
  );
};

export default BlogPostSEO;
